/**
 * Transcript cue parser — turns a raw transcript `body` (the free text stored
 * on a {@link import("@/stores/useTranscripts").Transcript} record) into a
 * list of timed cues the playback modal renders and syncs to an
 * `<audio>`/`<video>` element.
 *
 * Pure + dependency-free (no React, no stores) so it's trivially unit-tested
 * and reusable. Cues are derived at render time and NEVER persisted.
 *
 * Supported formats (detected most-specific first):
 *   - `vtt`       — WebVTT: `WEBVTT` header, `00:00:00.000 --> …` cues,
 *                   `<v Speaker>` voice tags, NOTE/STYLE/REGION blocks skipped.
 *   - `srt`       — SubRip: numbered blocks, `00:00:00,000 --> …` (comma ms).
 *   - `bracketed` — timestamp-led lines: `[mm:ss]` / `mm:ss` / `[hh:mm:ss]`,
 *                   optionally `Speaker: text`; untimed lines continue the prior cue.
 *   - `plain`     — no timestamps. Cues are still produced (so the text
 *                   renders) but `synced` is `false`, which disables
 *                   seek/highlight in the UI.
 *
 * Speaker inference for non-VTT formats is best-effort (a short `Name:` prefix
 * heuristic) — it only affects the displayed speaker column, never timing.
 */

export type Cue = {
  /** Stable, deterministic id (`<format>-<index>`) — used as the React key. */
  id: string;
  /** Cue start in milliseconds. `NaN` for untimed (plain) cues. */
  startMs: number;
  /** Cue end in milliseconds when the format carries one (VTT/SRT). */
  endMs?: number;
  /** Speaker label when present/inferable. */
  speaker?: string;
  /** Cleaned cue text (inline tags stripped, entities decoded). */
  text: string;
};

export type CueFormat = "vtt" | "srt" | "bracketed" | "plain";

export type ParsedTranscript = {
  cues: Cue[];
  /** True only when every cue has a finite `startMs` — gates seek/highlight. */
  synced: boolean;
  format: CueFormat;
};

/* ----------------------------- Normalisation --------------------------- */

/** Strip a leading BOM and normalise CRLF/CR to LF. */
function normalizeInput(raw: string): string {
  return raw.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");
}

/**
 * Parse a timestamp token (`HH:MM:SS.mmm`, `MM:SS,mmm`, `M:SS`, `MM:SS`…) to
 * milliseconds. Accepts `.` or `,` as the fractional separator. Returns `null`
 * when the token isn't a recognisable timestamp.
 *
 * Zero is a valid result (`00:00:00.000 → 0`); callers must use
 * `Number.isFinite`, never a truthy check.
 */
export function timestampToMs(token: string): number | null {
  const m = /^(?:(\d{1,2}):)?(\d{1,3}):(\d{1,2})(?:[.,](\d{1,3}))?$/.exec(token.trim());
  if (!m) return null;
  const hours = m[1] ? Number(m[1]) : 0;
  const minutes = Number(m[2]);
  const seconds = Number(m[3]);
  const frac = m[4] ? Number(m[4].padEnd(3, "0")) : 0;
  if (seconds > 59) return null;
  // Only constrain minutes < 60 when an hours field is present; bare `mm:ss`
  // may legitimately exceed 59 minutes (e.g. `75:30`).
  if (m[1] && minutes > 59) return null;
  return ((hours * 60 + minutes) * 60 + seconds) * 1000 + frac;
}

/** Format milliseconds back to a compact display label (`mm:ss` / `h:mm:ss`). */
export function formatCueTime(ms: number): string {
  if (!Number.isFinite(ms)) return "";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const mm = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/* ----------------------------- Detection ------------------------------- */

export function detectFormat(body: string): CueFormat {
  const text = normalizeInput(body);
  const firstNonEmpty = (text.split("\n").find((l) => l.trim() !== "") ?? "").trim();
  if (/^WEBVTT\b/.test(firstNonEmpty)) return "vtt";
  // SRT uses a comma before the ms; VTT uses a dot. A comma-ms `-->` anywhere
  // is a strong SubRip signal.
  if (/\d{1,2}:\d{2}:\d{2},\d{1,3}\s*-->/.test(text)) return "srt";
  // Any line led by a `[mm:ss]` / `mm:ss` timestamp.
  if (/^[ \t]*\[?\d{1,2}:\d{2}(?::\d{2})?(?:[.,]\d{1,3})?\]?[ \t]/m.test(text)) {
    return "bracketed";
  }
  return "plain";
}

/* ------------------------------- Helpers ------------------------------- */

/** Remove inline markup (`<v …>`, `<i>`, `<c.x>`, karaoke `<00:00.000>`…). */
function stripInlineTags(s: string): string {
  return s.replace(/<\/?[^>]+>/g, "");
}

/** Decode the handful of entities VTT/SRT carry (`&amp;` decoded last). */
function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

/** Parse a `start --> end <settings>` timing line; settings after `end` are dropped. */
function parseTimingLine(line: string): { startMs: number; endMs?: number } | null {
  const parts = line.split("-->");
  if (parts.length < 2) return null;
  const startMs = timestampToMs(parts[0] ?? "");
  if (startMs === null) return null;
  const endToken = (parts[1] ?? "").trim().split(/\s+/)[0] ?? "";
  const endMs = timestampToMs(endToken);
  return endMs !== null ? { startMs, endMs } : { startMs };
}

/** Extract a `<v Speaker>` voice tag (VTT) and strip remaining inline markup. */
function extractVttText(raw: string): { speaker?: string; text: string } {
  const trimmed = raw.trim();
  let speaker: string | undefined;
  let body = trimmed;
  const voice = /^<v(?:\.[^ >]+)*\s+([^>]+)>(.*)$/s.exec(trimmed);
  if (voice) {
    speaker = voice[1]?.trim();
    body = voice[2] ?? "";
  }
  const text = decodeEntities(stripInlineTags(body)).trim();
  return speaker ? { speaker, text } : { text };
}

/**
 * Best-effort `Speaker: utterance` split for SRT/bracketed/plain text. Only
 * treats the prefix as a speaker when it's short, capitalised, and free of
 * sentence punctuation — so `Note: the following…` stays a plain line.
 */
function splitSpeaker(s: string): { speaker?: string; text: string } {
  const m = /^([^:]{1,40}):\s+(\S.*)$/s.exec(s);
  if (m) {
    const candidate = (m[1] ?? "").trim();
    const looksLikeName =
      candidate.length > 0 &&
      /^[A-Z]/.test(candidate) &&
      candidate.split(/\s+/).length <= 4 &&
      !/[.!?]\s/.test(candidate);
    if (looksLikeName) return { speaker: candidate, text: (m[2] ?? "").trim() };
  }
  return { text: s };
}

function mkCue(id: string, startMs: number, text: string, speaker?: string, endMs?: number): Cue {
  const cue: Cue = { id, startMs, text };
  if (endMs !== undefined) cue.endMs = endMs;
  if (speaker) cue.speaker = speaker;
  return cue;
}

/* ------------------------------- Parsers ------------------------------- */

function parseBlocks(text: string): string[][] {
  return text
    .split(/\n{2,}/)
    .map((block) => block.split("\n").map((l) => l.replace(/[ \t]+$/, "")))
    .filter((lines) => lines.some((l) => l.trim() !== ""));
}

function parseVtt(text: string): Cue[] {
  const cues: Cue[] = [];
  let idx = 0;
  for (const lines of parseBlocks(text)) {
    const first = (lines[0] ?? "").trim();
    if (/^WEBVTT\b/.test(first)) continue; // header block
    if (/^(NOTE|STYLE|REGION)\b/.test(first)) continue; // metadata block
    const timingIdx = lines.findIndex((l) => l.includes("-->"));
    if (timingIdx === -1) continue;
    const timing = parseTimingLine(lines[timingIdx] ?? "");
    if (!timing) continue;
    const textLines = lines.slice(timingIdx + 1).filter((l) => l.trim() !== "");
    if (textLines.length === 0) continue;
    const { speaker, text: body } = extractVttText(textLines.join(" "));
    if (body === "") continue;
    cues.push(mkCue(`vtt-${idx++}`, timing.startMs, body, speaker, timing.endMs));
  }
  return cues;
}

function parseSrt(text: string): Cue[] {
  const cues: Cue[] = [];
  let idx = 0;
  for (const rawLines of parseBlocks(text)) {
    // Drop a leading numeric index line.
    const lines = /^\d+$/.test((rawLines[0] ?? "").trim()) ? rawLines.slice(1) : rawLines;
    const timingIdx = lines.findIndex((l) => l.includes("-->"));
    if (timingIdx === -1) continue;
    const timing = parseTimingLine(lines[timingIdx] ?? "");
    if (!timing) continue;
    const textLines = lines.slice(timingIdx + 1).filter((l) => l.trim() !== "");
    if (textLines.length === 0) continue;
    const joined = decodeEntities(stripInlineTags(textLines.join(" "))).trim();
    if (joined === "") continue;
    const { speaker, text: body } = splitSpeaker(joined);
    cues.push(mkCue(`srt-${idx++}`, timing.startMs, body, speaker, timing.endMs));
  }
  return cues;
}

function parseBracketed(text: string): Cue[] {
  const cues: Cue[] = [];
  const lead = /^[ \t]*\[?(\d{1,2}:\d{2}(?::\d{2})?(?:[.,]\d{1,3})?)\]?[ \t]*(.*)$/;
  let idx = 0;
  for (const rawLine of text.split("\n")) {
    const line = rawLine.replace(/[ \t]+$/, "");
    if (line.trim() === "") continue;
    const m = lead.exec(line);
    const startMs = m ? timestampToMs(m[1] ?? "") : null;
    if (m && startMs !== null) {
      const rest = (m[2] ?? "").trim();
      const { speaker, text: body } = splitSpeaker(rest);
      cues.push(mkCue(`ts-${idx++}`, startMs, speaker ? body : rest, speaker));
    } else if (cues.length > 0) {
      // Continuation line for the previous cue.
      const last = cues[cues.length - 1];
      if (last) last.text = last.text ? `${last.text} ${line.trim()}` : line.trim();
    }
  }
  return cues;
}

function parsePlain(text: string): Cue[] {
  const trimmed = text.trim();
  if (trimmed === "") return [];
  const paragraphs = trimmed.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  const units =
    paragraphs.length > 1
      ? paragraphs.map((p) => p.replace(/\n/g, " "))
      : trimmed.split("\n").map((l) => l.trim()).filter(Boolean);
  return units.map((u, i) => {
    const { speaker, text: body } = splitSpeaker(u);
    return mkCue(`plain-${i}`, Number.NaN, speaker ? body : u, speaker);
  });
}

/* ------------------------------- Public -------------------------------- */

export function parseTranscriptBody(body: string): ParsedTranscript {
  const text = normalizeInput(body ?? "");
  const format = detectFormat(text);
  let cues: Cue[];
  switch (format) {
    case "vtt":
      cues = parseVtt(text);
      break;
    case "srt":
      cues = parseSrt(text);
      break;
    case "bracketed":
      cues = parseBracketed(text);
      break;
    default:
      cues = parsePlain(text);
      break;
  }
  // A format may detect but parse nothing usable (malformed VTT/SRT) — fall
  // back to plain so the text still renders (un-synced).
  if (cues.length === 0 && format !== "plain") {
    return { cues: parsePlain(text), synced: false, format: "plain" };
  }
  const synced = cues.length > 0 && cues.every((c) => Number.isFinite(c.startMs));
  return { cues, synced, format };
}

/**
 * Binary search: index of the last cue whose `startMs <= currentMs`, or `-1`
 * when `currentMs` precedes the first cue. `cues` MUST be sorted ascending by
 * `startMs`.
 */
export function findActiveCueIndex(cues: Cue[], currentMs: number): number {
  let lo = 0;
  let hi = cues.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const cue = cues[mid];
    if (cue && cue.startMs <= currentMs) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}
